import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import Reveal from './Reveal';

const images = [
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/g6lv6pwz_Resepsiyon.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/8retqw65_Giri%C5%9F.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/vkyasyy0_2%2B1%20kapak.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/c89vdxyn_D-101.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/0wlrdbhw_D-102.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/t5z42cgg_1%2B1%20kapak.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/36on7b6g_D-107.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/ivyjvbkj_D-108.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/pus0be4z_D-109.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/l3is9rjt_D-110.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/k2yx573e_D-111.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/mabnsh0r_D-112.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/o7bjfmlw_D-113.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/4700utxs_D-114.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/wj8ocrn4_D-115.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/t8eipmqh_D-20%20Melih.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/ey5o9s1g_D-118.jpeg",
  "https://customer-assets.emergentagent.com/job_business-polish-1/artifacts/5ll8oc1g_D-122.jpeg"
];

const Gallery = () => {
  const [selectedIdx, setSelectedIdx] = useState(null);

  const nextImage = useCallback((e) => {
    e?.stopPropagation?.();
    setSelectedIdx((prev) => (prev + 1) % images.length);
  }, []);

  const prevImage = useCallback((e) => {
    e?.stopPropagation?.();
    setSelectedIdx((prev) => (prev - 1 + images.length) % images.length);
  }, []);

  // Klavyedeki yön tuşları ve ESC ile kontrol
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIdx === null) return;
      if (e.key === 'Escape') setSelectedIdx(null);
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedIdx, nextImage, prevImage]);

  // Lightbox açıkken arka plan kaydırmayı kilitle
  useEffect(() => {
    if (selectedIdx !== null) {
      window.__lenis?.stop();
      document.body.style.overflow = 'hidden';
    } else {
      window.__lenis?.start();
      document.body.style.overflow = '';
    }
  }, [selectedIdx]);

  return (
    <>
      <section id="gallery" className="py-24 bg-zinc-950 border-t border-zinc-900" data-testid="gallery-section">
        <div className="container mx-auto px-4 md:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <h2 className="text-sm font-bold text-yellow-500 tracking-[0.3em] uppercase mb-3">Oteli Keşfedin</h2>
              <h3 className="font-display text-3xl md:text-4xl font-semibold text-white">Galeri & Resimler</h3>
            </div>
            <p className="text-zinc-400 max-w-md text-sm md:text-base">
              Modern ve şık tasarıma sahip dairelerimizin detaylarını inceleyin. Sizin için her detayı özenle düşündük.
            </p>
          </Reveal>

          {images.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((src, idx) => (
                <Reveal key={idx} delay={(idx % 4) * 0.08}>
                  <div
                    className="relative overflow-hidden rounded-xl group h-48 md:h-64 cursor-pointer border border-zinc-800/60 hover:border-yellow-500/50 transition-colors duration-300"
                    onClick={() => setSelectedIdx(idx)}
                    data-testid={`gallery-item-${idx}`}
                  >
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10 flex items-center justify-center">
                      <span className="text-black font-semibold bg-yellow-500 px-5 py-2 rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 pointer-events-none">İncele</span>
                    </div>
                    <img
                      src={src}
                      alt={`İskenderun Konaklama Galeri ${idx + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 border border-dashed border-zinc-800 rounded-2xl bg-zinc-900/30">
              <p className="text-zinc-400 text-lg">Fotoğraflar düzenleniyor, yakında buraya eklenecektir.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox / Modal */}
      <AnimatePresence>
        {selectedIdx !== null && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
            onClick={() => setSelectedIdx(null)}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 z-50 text-white/70 hover:text-yellow-500 transition-colors bg-black/50 p-2 rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIdx(null);
              }}
              data-testid="lightbox-close"
              aria-label="Kapat"
            >
              <X size={32} />
            </button>

            <button
              className="absolute left-4 md:left-12 top-1/2 -translate-y-1/2 z-50 text-white/70 hover:text-yellow-500 transition-colors bg-black/50 p-3 rounded-full"
              onClick={prevImage}
              data-testid="lightbox-prev"
              aria-label="Önceki fotoğraf"
            >
              <ChevronLeft size={36} />
            </button>

            <motion.img
              key={selectedIdx}
              src={images[selectedIdx]}
              alt={`Büyük Boyut ${selectedIdx + 1}`}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              data-testid="lightbox-image"
            />

            <button
              className="absolute right-4 md:right-12 top-1/2 -translate-y-1/2 z-50 text-white/70 hover:text-yellow-500 transition-colors bg-black/50 p-3 rounded-full"
              onClick={nextImage}
              data-testid="lightbox-next"
              aria-label="Sonraki fotoğraf"
            >
              <ChevronRight size={36} />
            </button>

            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 bg-black/50 px-4 py-1 rounded-full font-medium tracking-widest text-sm"
              data-testid="lightbox-counter"
            >
              {selectedIdx + 1} / {images.length}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
